import { QueryClient } from "@tanstack/react-query";

import { ApiError } from "./client";

/**
 * Statuses no amount of retrying can change: the caller is not authenticated,
 * not authorized, the resource does not exist, the request named a value this
 * service refuses (422), or the Editor Service has not implemented the endpoint
 * yet (501 — see ApiError.isNotImplemented). Retrying these only delays the
 * screen that explains the situation and triples the request count against a
 * service that is already telling us the answer.
 *
 * ## Why 422 belongs here, for QUERIES
 *
 * A 422 is the service refusing a VALUE it was given, and a query's whole
 * request is its URL: the retry sends the identical path with the identical
 * parameters, so it gets the identical refusal. There is no equivalent of a
 * mutation's stale-write 409, where re-reading and resending genuinely changes
 * the outcome — and mutations do not consult this predicate anyway, because
 * `createQueryClient` sets `mutations.retry: false` outright.
 *
 * It is the same argument `TERMINAL_CONFLICT_CODES` below already makes about
 * queries, and it arrived through the one query route that answers 422:
 * `useCandidateContentsQuery` reads ONE immutable frozen candidate, and its
 * `422 candidateId` means the candidate has expired or is not this project's
 * (#430's own collapse). Asking again cannot un-expire it. Two automatic
 * retries against it bought nothing and delayed the sentence that tells the
 * clinician to freeze a new version (PR #482 review).
 *
 * Everything else is treated as transient and gets two retries with backoff —
 * 502/503/504 above all, which for that same route is the arm where a second
 * attempt a moment later genuinely can succeed.
 */
const TERMINAL_STATUSES = [401, 403, 404, 422, 501];

/**
 * 409s that no retry can clear either — but only these.
 *
 * A 409 is normally the most retryable failure there is: every `stale_*` code
 * means "someone moved first, re-read and resend", and the client recovers
 * mechanically. #29's section projection introduced the opposite kind. It
 * answers 409 for STORED STATE that disagrees with the runtime — a drifted
 * base pin, an unplaceable adoption, draft content the engine refuses — and
 * its own contract text says the recovery is a state change, not a retry.
 * Treating those as transient costs two extra requests that each re-run a
 * snapshot read plus (on a miss) an engine subprocess, and it offers the
 * clinician a "Try again" that cannot work — the exact control the 501 arm was
 * designed never to render (claude review, PR #317).
 *
 * Matched on CODE, not status, so the stale-version family keeps its retry
 * wherever this predicate is reused. Two notes on why this list is safe:
 *
 *  - it governs QUERIES — every consumer is a query surface, and none is a
 *    mutation. (Stated as a property rather than a count: an earlier version
 *    of this comment said "three consumers" when there were five across six
 *    call sites, and the count is the load-bearing part of the argument, so a
 *    reader taking it at face value would check the wrong subset.) A query is
 *    a GET, which carries no optimistic-concurrency token, so it cannot fail
 *    the stale-write way that makes a mutation's 409 recoverable by resending;
 *  - `conflict` is also what `CODE_BY_STATUS` falls back to when a 409 carries
 *    no envelope. That fallback lands here deliberately: a query that conflicts
 *    with stored state is not something the same request repeated will fix.
 *
 * `summary_structure_version_mismatch` (#639, joined by #640) is the third
 * member and the clearest case of the rule. The Summary report-structure read
 * asserts the structure version the browser build renders, and the service
 * refuses it when the deployment serves another one — its own contract text
 * says "there is nothing to resend". The served version cannot change between
 * two attempts a few hundred milliseconds apart, so a retry is a request that
 * is guaranteed to be refused identically, and the cached-data half matters as
 * much as the retry half: a build whose read is now refused must not keep
 * rendering the structure it read before the deployment moved.
 */
const TERMINAL_CONFLICT_CODES = [
  "projection_identity_mismatch",
  "conflict",
  "summary_structure_version_mismatch",
  "summary_presentation_registry_mismatch",
];

/**
 * Codes that are terminal WHATEVER status carries them.
 *
 * `directory_unavailable` covers missing provisioning and bounded upstream
 * failures, including throttling. Avoid automatic retries against Graph;
 * the picker and name-resolution notice provide explicit retries. The picker
 * hides stale directory search results.
 */
const TERMINAL_CODES = ["directory_unavailable"];

/**
 * A query failure that cached data must not obscure.
 *
 * Cached data is useful during a transient outage, but these statuses say the
 * caller may no longer see the resource, the resource is gone, or the endpoint
 * is unavailable by design. Screens fail closed instead of continuing to
 * expose the last successful response.
 */
export function isTerminalQueryError(error: unknown): error is ApiError {
  if (!(error instanceof ApiError)) {
    return false;
  }
  if (TERMINAL_STATUSES.includes(error.status)) {
    return true;
  }
  if (error.code !== undefined && TERMINAL_CODES.includes(error.code)) {
    return true;
  }
  return (
    error.status === 409 &&
    error.code !== undefined &&
    TERMINAL_CONFLICT_CODES.includes(error.code)
  );
}

/**
 * Why every default here sets `networkMode` explicitly.
 *
 * TanStack Query defaults `networkMode` to `"online"`, which does not fail a
 * request when the browser reports offline — it *pauses* it. A paused query
 * keeps `status: "pending"`, which every surface in this application renders as
 * a loading skeleton. That is the defect in issue #215: the transport is gone,
 * nothing on screen admits it, and there is no control to recover.
 *
 * `"offlineFirst"` fires the first attempt regardless of what the browser
 * believes about connectivity, so a failure becomes a real error the surfaces
 * can render. Retries are still paused while offline — that is deliberate, and
 * it is why surfaces must distinguish paused from loading rather than assume
 * `isPending` means "a request is in flight".
 *
 * ## What the paragraph above leaves out, and what it cost (#327)
 *
 * It accounts for ONE of the two clauses that hold a retry, and the missing one
 * is why a pause with `navigator.onLine === true` looked inexplicable to QA and
 * to the reporter of #327. `@tanstack/query-core` (5.101.4,
 * `build/modern/retryer.js`) resumes a retry only when
 *
 *     focusManager.isFocused()
 *       && (networkMode === "always" || onlineManager.isOnline())
 *       && config.canRun()
 *
 * `canRun()` is hard-coded `() => true` for queries, and `"offlineFirst"` is
 * not `"always"`, so the middle clause is the online one this comment already
 * described. The clause it did not describe is `focusManager.isFocused()`,
 * which is nothing more than `document.visibilityState !== "hidden"` — nothing
 * in this application calls `setFocused`, so it is purely the document's
 * visibility. A hidden tab therefore parks EVERY retry, including the retry of
 * a request the Editor Service already answered with a status.
 *
 * `networkMode` decides whether the FIRST attempt fires (`canStart` → `canFetch`,
 * which ignores focus). It exempts nothing from the focus clause. So `isPaused`
 * does not mean "the transport is gone", and a surface that reads it that way
 * tells a clinician they are offline while the service is answering — which is
 * the whole of #327. `isServerAnsweredFailure` below is the one place that
 * distinction is drawn.
 */
const NETWORK_MODE = "offlineFirst" as const;

/**
 * Whether a failure came back FROM the Editor Service rather than instead of it.
 *
 * `ApiError` is constructed in exactly one place — `toApiError`, from a
 * `Response` — so it exists if and only if the request completed and the
 * service put a status on it. A transport that never connected rejects with
 * whatever `fetch` threw, which is not an `ApiError`, and that is the only
 * condition the offline vocabulary may describe.
 *
 * This is the single source of truth for the distinction #327 exists to draw:
 * paused because the connection is gone, versus paused because a retry of an
 * *answered* request is being withheld (see the focus clause above). Both
 * surfaces that render a paused query — `QueryPendingState` and the shell's
 * `SessionGate` — decide it here rather than each testing `instanceof`, so the
 * two can never drift into disagreeing about what "offline" means.
 *
 * Only meaningful for a query that is actually paused: on a settled query the
 * same fact is `isError` plus `error`, and the surfaces already branch on that.
 *
 * A paused query's failure is also never terminal. `retry` below returns false
 * for `isTerminalQueryError`, and `retryer.ts` rejects on `!shouldRetry` before
 * it ever sleeps or pauses, so a 401, 403, 404, 501 or terminal 409 settles as
 * an error and never reaches a paused surface. What can reach one is the
 * transient set — 429, 5xx, and the 503 `upstream_unavailable` this ticket was
 * filed for — every member of which is worth offering a retry for.
 */
export function isServerAnsweredFailure(
  failureReason: unknown,
): failureReason is ApiError {
  return failureReason instanceof ApiError;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        networkMode: NETWORK_MODE,
        retry(failureCount, error) {
          if (isTerminalQueryError(error)) {
            return false;
          }
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
      },
      mutations: { networkMode: NETWORK_MODE, retry: false },
    },
  });
}
